// Profile Content for Modal (Applicant profile view)
// Adapted from Profile.js for viewing doctor profiles within modal

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesome5, FontAwesome6 } from '@expo/vector-icons';
import * as WebBrowser from 'expo-web-browser';
import WebModal from './WebModal';
import { getUser } from '../../services/profile';
import { approveJob, reapproveJob, rejectJob } from '../../services/job';
import { webColors, webBorderRadius, webSpacing } from '../../constants/webStyles';
import { normalize } from '../../utils/normalize';
import { extractProfessionTitleAndSubtitle } from '../../utils/professionUtils';
import { getAdministrativeDivisionLabel } from '../../constants/countries';
import { PLACEHOLDER_PROFILE_URL } from '../../constants/placeholderImage';
import { isFullTimeJob } from '../../constants/lov/professions';

const ProfileContent = ({
  applicantData,
  jobData,
  onBack,
  onRefresh,
}) => {
  const queryClient = useQueryClient();
  const { country } = useSelector((state) => state.user);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve' or 'reject'
  const isNonLocum = isFullTimeJob(jobData?.type);

  // Get doctor slug from applicant data
  const doctorSlug = applicantData?.doctor?.slug || applicantData?.slug;

  // Fetch full doctor profile
  const {
    data: profileQuery,
    isLoading: profileLoading,
    refetch: profileRefetch,
  } = useQuery({
    queryKey: ['doctorProfileModal', doctorSlug],
    queryFn: () => getUser(doctorSlug),
    enabled: !!doctorSlug,
  });
  const profileData = profileQuery?.data?.data;

  // Country-specific labels
  const divisionLabel = getAdministrativeDivisionLabel(country);

  const getMedicalLicenseName = (countryCode) => {
    const names = {
      'MY': 'MMC',
      'SG': 'SMC',
      'PH': 'PRC',
      'ID': 'STR'
    };
    return names[countryCode] || 'Medical Registration';
  };

  const getPracticeLicenseName = (countryCode) => {
    const names = {
      'MY': 'APC',
      'SG': 'PC',
      'PH': 'PRC-ID',
      'ID': 'SIP'
    };
    return names[countryCode] || 'Practice License';
  };

  const getIdDocumentLabel = (countryCode) => {
    const labels = {
      'MY': 'IC Number',
      'SG': 'NRIC Number',
      'PH': 'Valid ID',
      'ID': 'KTP Number'
    };
    return labels[countryCode] || 'National ID Number';
  };

  // Mutations
  const approveMutation = useMutation({
    mutationFn: jobData?.status === -2 ? reapproveJob : approveJob,
    onSuccess: () => {
      setShowConfirmModal(false);
      queryClient.invalidateQueries({ queryKey: ['jobApplicationsModal'] });
      setTimeout(() => {
        alert('Approved! You have successfully approved this applicant.');
      }, 300);
      onRefresh?.();
      onBack?.();
    },
    onError: (error) => {
      setShowConfirmModal(false);
      alert('Oops! ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  const rejectMutation = useMutation({
    mutationFn: rejectJob,
    onSuccess: () => {
      setShowConfirmModal(false);
      queryClient.invalidateQueries({ queryKey: ['jobApplicationsModal'] });
      setTimeout(() => {
        alert('Rejected. The applicant has been rejected.');
      }, 300);
      onRefresh?.();
      onBack?.();
    },
    onError: (error) => {
      setShowConfirmModal(false);
      alert('Oops! ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  const handleApprove = () => {
    setConfirmAction('approve');
    setShowConfirmModal(true);
  };

  const handleReject = () => {
    setConfirmAction('reject');
    setShowConfirmModal(true);
  };

  const confirmActionHandler = () => {
    if (confirmAction === 'approve') {
      approveMutation.mutate({ uuid: applicantData.uuid });
    } else if (confirmAction === 'reject') {
      rejectMutation.mutate({ uuid: applicantData.uuid });
    }
  };

  // Get applicant status
  const getApplicantStatus = () => {
    const statusCode = applicantData?.status_code ?? applicantData?.status ?? applicantData?.job_application_status_code;
    if (statusCode === 3 || statusCode === 4) return 'approved';
    if (statusCode === 1 || statusCode === 2) return 'pending-applicant';
    if (statusCode === -1 || statusCode === -2) return 'declined';
    if (statusCode === -4) return 'cancelled';
    if (statusCode === 0 && isNonLocum) return 'applied';
    return 'pending';
  };

  const status = getApplicantStatus();
  const canApprove = !isNonLocum && (status === 'pending' || status === 'pending-applicant');
  const canReject = !isNonLocum && (status === 'pending' || status === 'pending-applicant');

  // Title-Value display helper
  const TitleValue = ({ title, value, required = false }) => (
    <View style={styles.fieldContainer}>
      <Text style={styles.fieldTitle}>
        {title} {required && !value && <Text style={styles.required}>*</Text>}
      </Text>
      <Text style={styles.fieldValue}>{value || '-'}</Text>
    </View>
  );

  // Section Title helper
  const SectionTitle = ({ icon, title }) => (
    <View style={styles.sectionHeader}>
      <FontAwesome5 name={icon} size={14} color={webColors.textPrimary} />
      <Text style={styles.sectionTitle}>{title}</Text>
    </View>
  );

  if (profileLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={webColors.primary} />
      </View>
    );
  }

  const doctor = profileData || applicantData?.doctor || {};
  const fullName = doctor.first_name
    ? `${doctor.first_name} ${doctor.last_name || ''}`
    : applicantData?.name || 'Applicant';

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Profile Header */}
        <View style={styles.profileHeader}>
          <View style={styles.avatarContainer}>
            <Image
              source={{ uri: doctor.profile_image
                ? 'data:image/jpg;base64,' + doctor.profile_image
                : PLACEHOLDER_PROFILE_URL }}
              style={styles.avatar}
            />
            {doctor.is_approved && (
              <View style={styles.verifiedBadge}>
                <FontAwesome5 name="check" size={10} color="#ffffff" />
              </View>
            )}
          </View>
          <Text style={styles.profileName}>{fullName}</Text>
          <Text style={styles.profileSubtitle}>{doctor.city || 'Location'}</Text>

          {/* Stats Row */}
          <View style={styles.statsRow}>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{doctor.completed_jobs || 0}</Text>
              <Text style={styles.statLabel}>Jobs</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{doctor.hours_worked?.toFixed(0) || 0}</Text>
              <Text style={styles.statLabel}>Hours</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{doctor.average_rating || 'N/A'}</Text>
              <Text style={styles.statLabel}>Rating</Text>
            </View>
          </View>
        </View>

        {/* Personal Information */}
        <View style={styles.section}>
          <SectionTitle icon="user" title="Personal Information" />
          <TitleValue title="About" value={doctor.about} />
          <TitleValue title="Phone" value={doctor.phone_number} />
          <TitleValue title="Email" value={doctor.email} />
        </View>

        {/* Profession */}
        {(doctor.professions?.length > 0 || doctor.profession) && (
          <View style={styles.section}>
            <SectionTitle icon="briefcase-medical" title="Profession" />
            {doctor.professions?.length > 0 ? (
              <View style={styles.professionsContainer}>
                {doctor.professions.map((prof, index) => {
                  const professionName = prof.malaysia_name || prof.singapore_name || prof.international_name;
                  const { title, subtitle } = extractProfessionTitleAndSubtitle(professionName);
                  return (
                    <View key={index} style={styles.professionPill}>
                      <Text style={styles.professionPillText}>{title}</Text>
                      {subtitle && (
                        <Text style={styles.professionPillSubtext}>{subtitle}</Text>
                      )}
                    </View>
                  );
                })}
              </View>
            ) : doctor.profession ? (
              <Text style={styles.fieldValue}>
                {doctor.profession.malaysia_name || doctor.profession.international_name || 'Healthcare Professional'}
              </Text>
            ) : null}
          </View>
        )}

        {/* Medical License */}
        {(doctor.apc || doctor.mmc) && (
          <View style={styles.section}>
            <SectionTitle icon="address-card" title="Medical License" />
            {doctor.apc && <TitleValue title={`${getPracticeLicenseName(country)} No.`} value={doctor.apc} />}
            {doctor.mmc && <TitleValue title={`${getMedicalLicenseName(country)} No.`} value={doctor.mmc} />}
            {doctor.nsr && <TitleValue title="NSR No." value={doctor.nsr} />}
          </View>
        )}

        {/* Education */}
        {doctor.education?.length > 0 && (
          <View style={styles.section}>
            <SectionTitle icon="user-graduate" title="Education" />
            {doctor.education.map((edu, index) => (
              <View key={index} style={styles.educationItem}>
                <Text style={styles.educationName}>{edu.name}</Text>
                <Text style={styles.educationDetails}>{edu.year} - {edu.school}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Resume */}
        {doctor.resume && (
          <View style={styles.section}>
            <SectionTitle icon="file-alt" title="Resume" />
            <TouchableOpacity
              style={styles.resumeBtn}
              onPress={() => WebBrowser.openBrowserAsync(doctor.resume)}
            >
              <FontAwesome5 name="file-pdf" size={18} color={webColors.primary} />
              <Text style={styles.resumeBtnText}>View Uploaded Resume</Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={{ height: 100 }} />
      </ScrollView>

      {/* Action Buttons (Fixed at bottom) */}
      {(canApprove || canReject) && (
        <View style={styles.actionBar}>
          {canReject && (
            <TouchableOpacity
              style={styles.rejectBtn}
              onPress={handleReject}
              disabled={rejectMutation.isPending}
            >
              <Text style={styles.rejectBtnText}>Reject</Text>
            </TouchableOpacity>
          )}
          {canApprove && (
            <TouchableOpacity
              style={[styles.approveBtn, approveMutation.isPending && styles.btnDisabled]}
              onPress={handleApprove}
              disabled={approveMutation.isPending}
            >
              <Text style={styles.approveBtnText}>
                {approveMutation.isPending ? 'Processing...' : 'Approve'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Status Badge for non-actionable states */}
      {!canApprove && !canReject && (
        <View style={styles.statusBar}>
          <View style={[
            styles.statusBadge,
            status === 'approved' && styles.statusApproved,
            status === 'applied' && styles.statusApproved,
            status === 'declined' && styles.statusDeclined,
            status === 'cancelled' && styles.statusCancelled,
          ]}>
            <Text style={[
              styles.statusBadgeText,
              status === 'approved' && styles.statusApprovedText,
              status === 'applied' && styles.statusApprovedText,
              status === 'declined' && styles.statusDeclinedText,
              status === 'cancelled' && styles.statusCancelledText,
            ]}>
              {status === 'approved' ? 'Approved' :
               status === 'applied' ? 'Applied' :
               status === 'declined' ? 'Declined' :
               status === 'cancelled' ? 'Cancelled' : status}
            </Text>
          </View>
        </View>
      )}

      {/* Confirmation Modal */}
      <WebModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={confirmAction === 'approve' ? 'Approve Applicant' : 'Reject Applicant'}
        maxWidth={400}
      >
        <View style={styles.confirmModalContent}>
          <Text style={styles.confirmText}>
            {confirmAction === 'approve'
              ? `Are you sure you want to approve ${fullName} for this job?`
              : `Are you sure you want to reject ${fullName}?`}
          </Text>
          <View style={styles.confirmButtons}>
            <TouchableOpacity
              style={styles.confirmCancelBtn}
              onPress={() => setShowConfirmModal(false)}
            >
              <Text style={styles.confirmCancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.confirmOkBtn,
                confirmAction === 'reject' && styles.confirmRejectBtn,
                (approveMutation.isPending || rejectMutation.isPending) && styles.btnDisabled,
              ]}
              onPress={confirmActionHandler}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            >
              <Text style={styles.confirmOkText}>
                {(approveMutation.isPending || rejectMutation.isPending)
                  ? 'Processing...'
                  : confirmAction === 'approve' ? 'Approve' : 'Reject'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </WebModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  scrollView: {
    flex: 1,
  },
  // Profile Header
  profileHeader: {
    alignItems: 'center',
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
    marginBottom: 20,
  },
  avatarContainer: {
    position: 'relative',
    marginBottom: 12,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  avatarPlaceholder: {
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.primary,
  },
  verifiedBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#22c55e',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#ffffff',
  },
  profileName: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  profileSubtitle: {
    fontSize: 14,
    color: webColors.textMuted,
    marginBottom: 16,
  },
  // Stats
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  statLabel: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 2,
  },
  statDivider: {
    width: 1,
    height: 30,
    backgroundColor: webColors.borderLight,
  },
  // Section
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  // Field
  fieldContainer: {
    marginBottom: 12,
  },
  fieldTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textMuted,
    marginBottom: 4,
  },
  fieldValue: {
    fontSize: 14,
    color: webColors.textPrimary,
    lineHeight: 20,
  },
  required: {
    color: '#ef4444',
  },
  // Profession Pills
  professionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  professionPill: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  professionPillText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  professionPillSubtext: {
    fontSize: 11,
    color: 'rgba(0,0,0,0.5)',
    marginTop: 2,
  },
  // Education
  educationItem: {
    marginBottom: 12,
  },
  educationName: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  educationDetails: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  // Resume Button
  resumeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: 12,
    borderRadius: 8,
    gap: 10,
  },
  resumeBtnText: {
    fontSize: 14,
    color: webColors.primary,
    fontWeight: '500',
  },
  // Action Bar
  actionBar: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: webColors.borderLight,
  },
  rejectBtn: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: '#fee2e2',
    borderRadius: 10,
    alignItems: 'center',
  },
  rejectBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ef4444',
  },
  approveBtn: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: '#22c55e',
    borderRadius: 10,
    alignItems: 'center',
  },
  approveBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  btnDisabled: {
    opacity: 0.6,
  },
  // Status Bar
  statusBar: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: webColors.borderLight,
    alignItems: 'center',
  },
  statusBadge: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#f3f4f6',
  },
  statusBadgeText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  statusApproved: {
    backgroundColor: '#dcfce7',
  },
  statusApprovedText: {
    color: '#22c55e',
  },
  statusDeclined: {
    backgroundColor: '#fee2e2',
  },
  statusDeclinedText: {
    color: '#ef4444',
  },
  statusCancelled: {
    backgroundColor: '#f3f4f6',
  },
  statusCancelledText: {
    color: '#6b7280',
  },
  // Confirm Modal
  confirmModalContent: {
    padding: 8,
  },
  confirmText: {
    fontSize: 15,
    color: webColors.textPrimary,
    marginBottom: 20,
    lineHeight: 22,
  },
  confirmButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  confirmCancelBtn: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    alignItems: 'center',
  },
  confirmCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  confirmOkBtn: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: '#22c55e',
    borderRadius: 8,
    alignItems: 'center',
  },
  confirmRejectBtn: {
    backgroundColor: '#ef4444',
  },
  confirmOkText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
});

export default ProfileContent;
